import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { FileText, LinkIcon, PlusCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ReferralURLConfig, ReferralTemplateConfig, saveReferralTemplate, deleteReferralTemplate } from '@/utils/config';
import { Card, CardContent } from '@/components/ui/card';
import TemplateList from './referral/TemplateList';
import TemplateForm from './referral/TemplateForm';
import TemplatePreviewDialog from './referral/TemplatePreviewDialog';
import type { TemplateFormData } from './referral/TemplateForm';
import useCampaigns from '@/hooks/useCampaigns';
import { CampaignIcon } from './icons/CustomIcons';

interface ReferralTemplateFormProps {
  templates: ReferralTemplateConfig[];
  urls: ReferralURLConfig[];
  onSaved: () => void;
}

const ReferralTemplateForm: React.FC<ReferralTemplateFormProps> = ({ 
  templates, 
  urls, 
  onSaved 
}) => {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({
    template: '',
    referralLink: 'https://example.com/ref/ABC123'
  });
  const { campaigns, loading: campaignsLoading } = useCampaigns();
  const activeCampaigns = campaigns.filter(c => c.isActive);

  const handleEdit = (template: ReferralTemplateConfig) => {
    setEditingId(template.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteReferralTemplate(id);
        toast({
          title: 'Template deleted',
          description: 'The referral template has been deleted successfully',
        });
        onSaved();
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to delete template',
          variant: 'destructive',
        });
      }
    }
  };

  const handlePreview = (template: string) => {
    setPreviewData({
      template,
      referralLink: 'https://example.com/ref/ABC123'
    });
    setIsPreviewOpen(true);
  };

  const handleSubmit = async (data: TemplateFormData) => {
    setIsSubmitting(true);
    
    try {
      // Get the current template for edits
      const currentTemplate = editingId ? templates.find(t => t.id === editingId) : null;
      
      const configToSave: ReferralTemplateConfig = {
        id: editingId || String(Date.now()),
        name: data.name,
        subject: "Join our referral program",
        messageTemplate: data.template,
        urlId: data.linkSource === 'url' ? data.urlConfigId : undefined,
        campaignId: data.linkSource === 'campaign' ? data.campaignId : undefined,
        isDefault: data.isDefault,
        template: data.template,
        urlConfigId: data.linkSource === 'url' ? data.urlConfigId : undefined,
        // Don't include createdAt since it's not in the type
      };
      
      await saveReferralTemplate(configToSave);
      
      toast({
        title: 'Success',
        description: `Referral template ${editingId ? 'updated' : 'added'} successfully`,
      });
      
      setEditingId(null);
      setShowAddForm(false);
      onSaved();
      
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save template',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUrlNameById = (id: string) => {
    const url = urls.find(u => u.id === id);
    return url ? url.name : 'Unknown URL';
  };

  const getCampaignNameById = (id: string) => {
    const campaign = campaigns.find(c => c.id === id);
    return campaign ? campaign.title : 'Unknown Campaign';
  };

  const getSourceText = (template: ReferralTemplateConfig) => {
    if (template.campaignId) {
      return `Campaign: ${getCampaignNameById(template.campaignId)}`;
    } else if (template.urlId || template.urlConfigId) {
      return `URL: ${getUrlNameById(template.urlId || template.urlConfigId || '')}`;
    }
    return 'No source selected';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Referral Templates</h3>
        {!showAddForm && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1"
            disabled={urls.length === 0 && activeCampaigns.length === 0}
          >
            <PlusCircle className="h-4 w-4" />
            Add Template
          </Button>
        )}
      </div>
      
      {urls.length === 0 && activeCampaigns.length === 0 && (
        <Alert>
          <CampaignIcon className="h-4 w-4" />
          <AlertTitle>No URLs or active campaigns configured</AlertTitle>
          <AlertDescription>
            You need to add at least one referral URL or active campaign before creating templates.
          </AlertDescription>
        </Alert>
      )}
      
      {(urls.length > 0 || activeCampaigns.length > 0) && templates.length === 0 && !showAddForm && (
        <Alert>
          <FileText className="h-4 w-4" />
          <AlertTitle>No templates configured</AlertTitle>
          <AlertDescription>
            Add templates that will be used for referring users.
          </AlertDescription>
        </Alert>
      )}
      
      {templates.length > 0 && (
        <TemplateList
          templates={templates}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onPreview={handlePreview}
          onSetDefault={(id) => {
            const template = templates.find(t => t.id === id);
            if (template) {
              handleSubmit({
                ...template,
                linkSource: template.campaignId ? 'campaign' : 'url',
                isDefault: true,
              });
            }
          }}
          getUrlNameById={getUrlNameById}
          getSourceText={getSourceText}
        />
      )}
      
      {showAddForm && (urls.length > 0 || activeCampaigns.length > 0) && (
        <Card>
          <CardContent className="pt-6">
            <TemplateForm
              urls={urls}
              initialData={editingId ? {
                ...templates.find(t => t.id === editingId),
                linkSource: templates.find(t => t.id === editingId)?.campaignId ? 'campaign' : 'url'
              } : undefined}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              onCancel={() => {
                setEditingId(null);
                setShowAddForm(false);
              }}
            />
          </CardContent>
        </Card>
      )}

      <TemplatePreviewDialog
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        template={previewData.template}
        referralLink={previewData.referralLink}
      />
    </div>
  );
};

export default ReferralTemplateForm;
